import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import FullScreenLoader from 'components/elements/Spinner/FullScreenLoader';
import ToggleSwitch from 'components/elements/Toggles/ToggleSwitch';
import { format, roundToNearestMinutes } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import BookingService, { BookingProps } from 'services/hooks/booking-service';
import { generateApiFailErrorMessage } from 'utils/helpers';
import { Table } from '../Common/Table';
import Delete from './BookingForms/delete';
import Save from './BookingForms/save';

interface Column {
  id: string;
  Header: string;
  accessor: any;
  disableFilters: boolean;
  Cell?: any;
  width?: number;
  disableSortBy?: boolean;
}

export default function BookingSaveMode() {
  const [getBookingsResult, setGetBookingsResult] = useState<BookingProps[] | null>([]);
  const [timeToggleValue, setTimeToggleValue] = useState<string>('upcoming');
  const [pageIndexResetTrigger, setpageIndexResetTrigger] = useState<number>(0);

  const handleAlignment = (newAlignment: string | null) => {
    if (newAlignment !== null) {
      setTimeToggleValue(newAlignment);
      setpageIndexResetTrigger((prev) => prev + 1);
    }
  };

  const fetchBookings = useCallback(async () => {
    return timeToggleValue === 'upcoming'
      ? BookingService.findAllBookings()
      : BookingService.findAllPastBookings();
  }, [timeToggleValue]);

  const {
    isLoading: isLoadingBookings,
    refetch,
    data: bookingData
  } = useQuery<BookingProps[], Error>(['bookings', timeToggleValue], fetchBookings, {
    onError: () => {
      toast.error(generateApiFailErrorMessage('Bookings'));
    }
  });

  useEffect(() => {
    if (bookingData && Array.isArray(bookingData)) {
      setGetBookingsResult(bookingData);
    }
  }, [bookingData]);

  const shortText = (text: string, count: number) =>
    text?.slice(0, count) + (text?.length > count ? '...' : '');

  const columns = useMemo(() => {
    const baseColumns: Column[] = [
      {
        id: 'building',
        Header: 'Building',
        accessor: (row: { buildingName: string; building: string }) =>
          row.buildingName && shortText(row.buildingName, 50),
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'bookedBy',
        Header: 'Booked By',
        accessor: (row: {
          MobileUser: { firstName: string; lastName: string };
          outlookBooking?: boolean;
          outlookOrganizer?: string;
        }) => {
          return row.outlookBooking
            ? row.outlookOrganizer
            : `${row.MobileUser.firstName} ${row.MobileUser.lastName}`;
        },
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'reason',
        Header: 'Reason',
        accessor: (row: { reason: string }) => (row.reason ? shortText(row.reason, 50) : 'None'),
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'amenity',
        Header: 'Amenity',
        accessor: (row: { resourceName: string; resource: string }) =>
          row.resourceName && shortText(row.resourceName, 50),
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'date',
        Header: 'Date',
        accessor: (row: { start: string; buildingTimezone: string }) => {
          const timeZone = row.buildingTimezone;
          const startDate = utcToZonedTime(new Date(row.start), timeZone);
          return startDate;
        },
        disableFilters: true,
        disableSortBy: false,
        Cell: ({ value }: { value: Date }) => {
          const formattedDate = format(value, 'PP');
          return formattedDate;
        }
      },
      {
        id: 'time',
        Header: 'Time',
        accessor: (row: { start: string; end: string; buildingTimezone: string }) => {
          const timeZone = row.buildingTimezone;

          // Convert start and end date and time to Date format (in provided timezone)
          const startDate = utcToZonedTime(new Date(row.start), timeZone);
          const endDate = utcToZonedTime(new Date(row.end), timeZone);

          //Below code is formatting the `startDate` variable to a string representation of the time in the format "h:mm a".
          const formattedStartTime = format(startDate, 'p');
          // Below code is formatting the `endDate` variable to a string representation of the time in the format "h:mm a".
          const formattedEndTime = format(roundToNearestMinutes(endDate, { nearestTo: 15 }), 'p');

          return `${formattedStartTime} - ${formattedEndTime}`;
        },
        disableFilters: true
      }
    ];

    if (timeToggleValue === 'upcoming') {
      baseColumns.push({
        id: 'actions',
        Header: 'Actions',
        accessor: (data: any) => data,
        Cell: (row: any) => {
          // Convert start and end date and time to Date format (in provided timezone)
          const startDate = utcToZonedTime(new Date(row.value.start), row.value.buildingTimezone);
          const endDate = utcToZonedTime(new Date(row.value.end), row.value.buildingTimezone);

          // Get current date and time in the building's timezone
          const currentTimeInTimezone = utcToZonedTime(new Date(), row.value.buildingTimezone);

          // Check if either startDate or endDate are in the past relative to the building's timezone
          const isOngoingOrPastBooking =
            startDate < currentTimeInTimezone || endDate < currentTimeInTimezone;

          return (
            <div className="flex gap-4">
              {!isOngoingOrPastBooking && row.value.outlookBooking === false && (
                <SecondaryButton
                  onClick={() => {
                    row.setEditUUID(row.value.uuid);
                    row.setUpdateShowModal(true);
                  }}
                  buttonLabel="Edit"
                  variant="default"
                  size="px-4 w-auto"
                />
              )}
              <SecondaryButton
                onClick={() => {
                  row.setDeleteUUID(row.value.uuid);
                  row.setResourceUUID(row.value.resource);
                  row.setDeleteShowModal(true);
                }}
                buttonLabel="Delete"
                variant="default"
                size="px-4 w-auto"
              />
            </div>
          );
        },
        width: 150,
        disableFilters: true
      });
    }

    return baseColumns;
  }, [timeToggleValue]);

  if (!getBookingsResult) {
    return <FullScreenLoader />;
  }

  return (
    <div className="manage-bookings" style={{ marginTop: 40 }}>
      <Table
        name="Bookings"
        refetch={refetch}
        columns={columns}
        data={getBookingsResult}
        CreateForm={Save}
        DeleteForm={Delete}
        createQuery={BookingService.createBooking}
        updateQuery={BookingService.updateBooking}
        deleteQuery={BookingService.deleteBookingById}
        editQuery={BookingService.findBookingById}
        queryName="bookingsManage"
        buttonName="Booking"
        isLoading={isLoadingBookings}
        gotoPageZero={pageIndexResetTrigger}
        topElement={
          <ToggleSwitch
            option1="upcoming"
            option2="past"
            selectedOption={timeToggleValue}
            onChange={handleAlignment}
          />
        }
      />
    </div>
  );
}
